<script lang="ts">
/**
 * The bank icons are served straight out of public/images, which links to
 * resources/images, so the page only fetches the one icon a row shows instead of
 * bundling every brand into the script.
 */
const ICON_DIRECTORY = "/images/banks";
const UNKNOWN_ICON = `${ICON_DIRECTORY}/unknown.png`;

/**
 * The icon file a brand is stored under. Brands arrive capitalised and spaced
 * ("American Express", "Diners Club"), the files are lowercase and underscored.
 */
export function brandIconKey(brand: string): string {
  return brand.toLowerCase().replace(/\s+/g, "_");
}
</script>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import useTranslation from "@Composables/useTranslation";

//
// Setup
const { __ } = useTranslation();

//
// Props
interface Props {
  brand: string;
}

const props = defineProps<Props>();

//
// Data
const hasNoIcon = ref<boolean>(false);

//
// Computed
const iconUrl = computed<string>(() => {
  return hasNoIcon.value ? UNKNOWN_ICON : `${ICON_DIRECTORY}/${brandIconKey(props.brand)}.png`;
});

//
// Methods
/**
 * A brand without a file of its own shows the unknown icon once the browser reports
 * the miss.
 */
function showUnknownIcon(): void {
  hasNoIcon.value = true;
}

//
// Watchers
watch(() => props.brand, () => {
  hasNoIcon.value = false;
});

//
// Exposed Test Vars
defineExpose({ iconUrl, hasNoIcon });
</script>

<template>
  <img
    class="payment-method-brand-icon"
    :src="iconUrl"
    :alt="__('settings_billing_page.card_icon_alt')"
    @error="showUnknownIcon"
  >
</template>

<style lang="scss" scoped>
.payment-method-brand-icon {
  height: 15px;
  width: auto;
}
</style>
